// ─── UTILITIES ────────────────────────────────────────────────────────────────
// Module-level pure functions extracted from CRM.jsx.
// No React imports or hooks — safe to import anywhere.

export const fmt = (n) => new Intl.NumberFormat("fr-CA",{style:"currency",currency:"CAD"}).format(n||0);
export const calcEstimateTotal = (est) => { let s=0; (est.sections||[]).filter(sec=>sec.type!=="group").forEach(sec=>(sec.items||[]).filter(i=>i.type!=="subheading").forEach(it=>{s+=(it.unitPrice||0)*(it.qty||1);})); return s; };
export const countItems = (est, type) => { if (type === 'invoice') return (est.sections||[]).length; let n=0; (est.sections||[]).forEach(s=>{n+=(s.items||[]).filter(i=>i.type!=="subheading").length;}); return n; };
export const avatarColor = (name) => { const c=["#b91c1c","#2563eb","#16a34a","#d97706","#7c3aed","#0891b2"]; let h=0; for(let ch of (name||"")) h=(h*31+ch.charCodeAt(0))%c.length; return c[h]; };

// ─── PHONE ────────────────────────────────────────────────────────────────────
export const formatPhone = (raw) => {
  const d = raw.replace(/\D/g,"").slice(0,10);
  if (d.length <= 3) return d.length ? "("+d : "";
  if (d.length <= 6) return "("+d.slice(0,3)+") "+d.slice(3);
  return "("+d.slice(0,3)+") "+d.slice(3,6)+"-"+d.slice(6);
};

// ─── GOOGLE PLACES ───────────────────────────────────────────────────────────
let _placesScriptLoading = null;
export const loadPlacesScript = () => {
  console.log('[Places] loadPlacesScript called. Key:', process.env.REACT_APP_PLACES_KEY ? 'found' : 'MISSING');
  if (window.google?.maps?.places) { console.log('[Places] Already loaded, resolving immediately.'); return Promise.resolve(); }
  if (_placesScriptLoading) { console.log('[Places] Script load already in progress.'); return _placesScriptLoading; }
  console.log('[Places] Injecting script tag...');
  _placesScriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_PLACES_KEY}&libraries=places`;
    s.async = true;
    s.onload = () => { console.log('[Places] Script loaded successfully.'); resolve(); };
    s.onerror = (e) => { console.error('[Places] Script failed to load:', e); _placesScriptLoading = null; reject(new Error("Places script failed")); };
    document.head.appendChild(s);
  });
  return _placesScriptLoading;
};

// ─── JOB / PHASE DATE MATH ────────────────────────────────────────────────────
export function calcPhaseDate(startStr, hoursPerDay, crewSize, phaseHours) {
  const daysNeeded = Math.ceil(phaseHours / Math.max(1, hoursPerDay * crewSize));
  return Math.max(1, daysNeeded);
}

export function addWorkdays(dateStr, days) {
  const d = new Date(dateStr + "T12:00:00");
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d.toISOString().split("T")[0];
}

export function buildPhaseDates(phases, startDate, hoursPerDay) {
  let current = startDate;
  return phases.map(ph => {
    const daysNeeded = calcPhaseDate(current, hoursPerDay, ph.crewSize, ph.labourHours);
    const phStart = current;
    const phEnd = daysNeeded <= 1 ? current : addWorkdays(current, daysNeeded - 1);
    current = addWorkdays(phEnd, 1);
    return { ...ph, startDate: phStart, endDate: phEnd, daysNeeded };
  });
}

export function extractPhasesFromEstimate(estimate, defaultCrewSize, defaultHoursPerDay) {
  return (estimate.sections||[]).filter(sec=>sec.type!=="group").map((sec, i) => {
    const labourItems = (sec.items||[]).filter(it => it.category==="Labor"||it.category==="Labour");
    const equipItems  = (sec.items||[]).filter(it => it.category==="Equipment");
    const matItems    = (sec.items||[]).filter(it => it.category==="Material");
    const labourHours = labourItems.reduce((a,it) => a + (it.qty||1), 0);
    return {
      id: `ph${i+1}_${Date.now()}`,
      title: sec.title,
      labourHours: labourHours || defaultHoursPerDay,
      crewSize: defaultCrewSize,
      crewName: "Crew A",
      showEquipment:true, showMaterials:true, showLabour:true,
      equipment: equipItems.map(it=>({name:it.name,qty:it.qty||1,unit:it.unit||""})),
      materials: matItems.map(it=>({name:it.name,qty:it.qty||1,unit:it.unit||""})),
      labourItems: labourItems.map(it=>({name:it.name,qty:it.qty||1,unit:it.unit||"hrs"})),
    };
  });
}

// ─── CREW COLORS ─────────────────────────────────────────────────────────────
export const CREW_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#d97706', // amber
  '#7c3aed', // purple
  '#0891b2', // cyan
  '#dc2626', // red
  '#db2777', // pink
  '#65a30d', // lime
];

export const getCrewColor = (crewName, teamMembers, jobs, scheduleNotes) => {
  if(!crewName) return '#6b7280';
  const member=(teamMembers||[]).find(m=>m.name?.trim().toLowerCase()===crewName?.trim().toLowerCase());
  if(member?.color) return member.color;
  const stableList = teamMembers&&teamMembers.length>0
    ? teamMembers.map(m=>m.name).filter(Boolean)
    : ['Crew A','Crew B'];
  const allCrews = [
    ...stableList,
    ...[...new Set([
      ...(jobs||[]).flatMap(j=>(j.crewNames&&j.crewNames.length>0)?j.crewNames:(j.crewName?[j.crewName]:[])),
      ...(scheduleNotes||[]).map(n=>n.crewName).filter(Boolean),
    ])].filter(c=>!stableList.includes(c)),
  ];
  const idx=allCrews.indexOf(crewName);
  return idx>=0?CREW_COLORS[idx%CREW_COLORS.length]:'#6b7280';
};

// ─── SCHEDULE CASCADE ─────────────────────────────────────────────────────────
export function cascadeForward(triggerJob, allJobs, allNotes) {
  const changes = [];
  const processed = new Set([triggerJob.id]);

  // Use crewId (new) for cascade; legacy crewNames[] jobs are treated as unassigned
  const getJobCrewId = (j) => j.crewId || null;

  const getEndDate = (item) => {
    if(item.phases&&item.phases.length>0)
      return item.phases.reduce((latest,ph)=>ph.endDate>latest?ph.endDate:latest,'');
    return item.endDate||item.startDate||'';
  };

  const getStartDate = (item) => {
    if(item.phases&&item.phases.length>0)
      return item.phases.reduce((earliest,ph)=>ph.startDate<earliest?ph.startDate:earliest,item.phases[0].startDate);
    return item.startDate||'';
  };

  const shiftItem = (item, type, daysNeeded) => {
    if(type==='note'){
      const start=new Date(item.startDate);
      const end=new Date(item.endDate||item.startDate);
      const duration=Math.round((end-start)/(1000*60*60*24));
      const newStart=addWorkdays(item.startDate,daysNeeded);
      const newEnd=addWorkdays(newStart,duration);
      return {startDate:newStart,endDate:newEnd};
    } else {
      const jobStart=new Date(getStartDate(item));
      const phases=(item.phases||[]).map(ph=>{
        const phStartOffset=Math.round((new Date(ph.startDate)-jobStart)/(1000*60*60*24));
        const phDuration=Math.round((new Date(ph.endDate)-new Date(ph.startDate))/(1000*60*60*24));
        const newPhStart=addWorkdays(addWorkdays(item.startDate,daysNeeded),phStartOffset);
        const newPhEnd=addWorkdays(newPhStart,phDuration);
        return {...ph,startDate:newPhStart,endDate:newPhEnd};
      });
      const newJobStart=addWorkdays(item.startDate,daysNeeded);
      const newJobEnd=addWorkdays(item.endDate||item.startDate,daysNeeded);
      return {startDate:newJobStart,endDate:newJobEnd,phases};
    }
  };

  let workingJobs=allJobs.map(j=>({...j,phases:j.phases?j.phases.map(p=>({...p})):[]}));

  // Ensure trigger job's new dates are reflected in working set
  const triggerIdx=workingJobs.findIndex(j=>j.id===triggerJob.id);
  if(triggerIdx>=0) workingJobs[triggerIdx]={...workingJobs[triggerIdx],...triggerJob,phases:triggerJob.phases?triggerJob.phases.map(p=>({...p})):workingJobs[triggerIdx].phases};

  const triggerEnd=getEndDate(workingJobs.find(j=>j.id===triggerJob.id)||triggerJob);

  const checkAndCascade=(changedId,changedEndDate,changedCrewId)=>{
    workingJobs.forEach(j=>{
      if(processed.has(j.id)) return;
      if(j.fixedDate) return;
      // Only cascade between jobs that share the same crewId
      if(!changedCrewId||!j.crewId||j.crewId!==changedCrewId) return;
      const jStart=getStartDate(j);
      if(!jStart||jStart>changedEndDate) return;
      const jEnd=getEndDate(j);
      const changedItem=workingJobs.find(jj=>jj.id===changedId)||{startDate:changedEndDate};
      if(jEnd<getStartDate(changedItem)) return;
      const daysToShift=Math.round((new Date(addWorkdays(changedEndDate,1))-new Date(jStart))/(1000*60*60*24));
      if(daysToShift<=0) return;
      const updates=shiftItem(j,'job',daysToShift);
      const idx=workingJobs.findIndex(jj=>jj.id===j.id);
      workingJobs[idx]={...workingJobs[idx],...updates};
      processed.add(j.id);
      changes.push({type:'job',id:j.id,updates});
      checkAndCascade(j.id,getEndDate(workingJobs[idx]),getJobCrewId(workingJobs[idx]));
    });
  };

  const triggerCrewId=getJobCrewId(triggerJob);
  checkAndCascade(triggerJob.id,triggerEnd,triggerCrewId);
  return changes;
}

// ─── GEO / TIME FORMATTING ────────────────────────────────────────────────────
export function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export function fmtDuration(ms) {
  if (!ms || ms < 0) return "0h00";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h${String(m).padStart(2,"0")}`;
}

// ─── REPORT / RECEIPT HTML GENERATORS ────────────────────────────────────────
// Pure functions — no React. Used by Invoices, Estimates, and Reports modules.

export function generateReceiptHTML(invoice, milestone, milestoneIdx, customer, settings) {
  const date = new Date().toLocaleDateString("fr-CA");
  const receiptNum = `${invoice.number||""}-R${milestoneIdx+1}`;
  const paidAmount = parseFloat(milestone.paidAmount)||milestone.amount||0;
  const sub = (invoice.sections||[]).reduce((a,s)=>{const p=s.price!=null?s.price:(s.items||[]).reduce((b,i)=>b+(i.unitPrice||0)*(i.qty||1),0);return a+p;},0);
  const tps = Math.round(sub*0.05*100)/100;
  const tvq = Math.round(sub*0.09975*100)/100;
  const contractTotal = sub+tps+tvq;
  const previouslyReceived = (invoice.paymentSchedule||[]).filter((_,i)=>i<milestoneIdx&&(invoice.paymentSchedule[i].status==="paid")).reduce((a,ms)=>a+(parseFloat(ms.paidAmount)||ms.amount||0),0);
  const balanceAfter = contractTotal-previouslyReceived-paidAmount;
  const methodLabels = {cheque:"Chèque / Cheque",virement:"Virement bancaire / Bank Transfer",carte:"Carte de crédit/débit / Credit/Debit Card",cash:"Argent comptant / Cash"};
  const methodLabel = methodLabels[milestone.paymentMethod]||milestone.paymentMethod||"—";
  const logoHTML = settings?.logoBase64
    ? `<img src="${settings.logoBase64}" style="height:52px;width:auto;object-fit:contain;" />`
    : `<div style="width:52px;height:52px;background:#b91c1c;border-radius:8px;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:14px;">${(settings?.companyName||"TAF").split(" ").slice(0,3).map(w=>w[0]).join("")}</div>`;
  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"/>
<title>Reçu ${receiptNum}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;font-size:12px;color:#1c1917;background:#fff;padding:28px 36px;max-width:720px;margin:0 auto;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #b91c1c;}
  .logo-block{display:flex;align-items:center;gap:14px;}
  .company-name{font-size:16px;font-weight:700;}
  .company-info{font-size:10.5px;color:#57534e;margin-top:3px;line-height:1.65;}
  .doc-type{font-size:19px;font-weight:700;color:#b91c1c;text-transform:uppercase;letter-spacing:.04em;}
  .doc-meta{font-size:10.5px;color:#57534e;margin-top:4px;line-height:1.8;}
  .client-section{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px;padding:11px 14px;background:#f8f7f5;border-radius:8px;}
  .section-label{font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#a8a29e;margin-bottom:4px;}
  .section-value{font-size:12px;color:#1c1917;line-height:1.6;}
  .box-title{font-size:9.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#a8a29e;margin-bottom:8px;}
  .receipt-box{border:1px solid #e5e3df;border-radius:8px;overflow:hidden;margin-bottom:18px;}
  .receipt-row{display:flex;justify-content:space-between;padding:9px 14px;font-size:13px;border-bottom:1px solid #e5e3df;}
  .receipt-row:last-child{border-bottom:none;}
  .receipt-row.highlight{background:#f0fdf4;font-weight:700;font-size:14px;color:#166534;}
  .balance-box{border:1px solid #e5e3df;border-radius:8px;overflow:hidden;margin-bottom:18px;}
  .balance-row{display:flex;justify-content:space-between;padding:8px 14px;font-size:12px;color:#57534e;border-bottom:1px solid #e5e3df;}
  .balance-row:last-child{border-bottom:none;}
  .balance-row.final{font-weight:700;font-size:13px;color:#1c1917;background:#f8f7f5;}
  .thank-you{text-align:center;padding:16px;background:#f0fdf4;border-radius:8px;margin-bottom:18px;font-size:13px;color:#166534;font-weight:600;border:1px solid #bbf7d0;}
  .footer{margin-top:18px;padding-top:10px;border-top:1px solid #e5e3df;text-align:center;font-size:10px;color:#a8a29e;}
  .no-print{margin-bottom:16px;display:flex;gap:8px;}
  @media print{.no-print{display:none;}button{display:none !important;}body{margin:0;padding:0;}@page{margin:15mm;}}
</style></head><body>
<div class="no-print">
  <button onclick="window.print()" style="padding:8px 18px;background:#b91c1c;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;font-weight:600;">🖨️ Imprimer / Print</button>
  <button onclick="window.close()" style="padding:8px 18px;background:#f3f4f6;color:#1c1917;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;">✕ Fermer</button>
</div>
<div class="header">
  <div class="logo-block">${logoHTML}<div><div class="company-name">${settings?.companyName||""}</div><div class="company-info">${settings?.address||""}${settings?.city?", "+settings.city:""} ${settings?.province||""} ${settings?.postal||""}<br/>${[settings?.phone,settings?.email].filter(Boolean).join(" · ")}<br/>${settings?.gst?"TPS: "+settings.gst+"  ":""}${settings?.qst?"TVQ: "+settings.qst:""}</div></div></div>
  <div style="text-align:right;"><div class="doc-type">Reçu de paiement</div><div style="font-size:10px;color:#a8a29e;margin-bottom:6px;">Payment Receipt</div><div class="doc-meta"><strong>N°:</strong> ${receiptNum}<br/><strong>Date:</strong> ${date}<br/><strong>Facture / Invoice:</strong> ${invoice.number||""}</div></div>
</div>
<div class="client-section">
  <div><div class="section-label">Client</div><div class="section-value"><strong>${customer?(customer.firstName+" "+customer.lastName):""}</strong><br/>${customer?.company?customer.company+"<br/>":""}${customer?.address?customer.address+"<br/>":""}${customer?.city?(customer.city+", "+customer.province+" "+customer.postal+"<br/>"):""}</div></div>
  <div><div class="section-label">Projet / Project</div><div class="section-value"><strong>${invoice.title||""}</strong></div></div>
</div>
<div class="box-title">Paiement reçu pour / Payment received for</div>
<div class="receipt-box">
  <div class="receipt-row"><span>${milestone.label}</span><span style="color:#a8a29e;">${milestone.percentage}% du total</span></div>
  <div class="receipt-row"><span>Mode / Method</span><span>${methodLabel}</span></div>
  <div class="receipt-row"><span>Date</span><span>${milestone.paidAt||date}</span></div>
  ${milestone.paymentNotes?`<div class="receipt-row"><span>Notes</span><span>${milestone.paymentNotes}</span></div>`:""}
  <div class="receipt-row highlight"><span>Montant reçu / Amount received</span><span>${fmt(paidAmount)}</span></div>
</div>
<div class="box-title">Sommaire du contrat / Contract summary</div>
<div class="balance-box">
  <div class="balance-row"><span>Total du contrat / Contract total</span><span>${fmt(contractTotal)}</span></div>
  <div class="balance-row"><span>Encaissé antérieurement / Previously received</span><span>${fmt(previouslyReceived)}</span></div>
  <div class="balance-row"><span>Ce paiement / This payment</span><span>${fmt(paidAmount)}</span></div>
  <div class="balance-row final"><span>Solde restant / Balance remaining</span><span style="color:${balanceAfter>0?"#b91c1c":"#166534"}">${fmt(balanceAfter)}</span></div>
</div>
<div class="thank-you">Merci pour votre paiement! / Thank you for your payment!</div>
<div class="footer">${settings?.companyName||""} ${settings?.website?"· "+settings.website:""} ${settings?.email?"· "+settings.email:""} · Document généré le ${date}</div>
</body></html>`;
}

export function generateReportHTML(type, estimate, customer, settings, config, priceList) {
  const cfg = { showLineItems:true, showUnitCost:true, showUnitPrice:true, showLineItemTotals:true, showSectionSubtotals:true, showTaxes:true, showReferenceMaterials:true, ...config };
  const date = new Date().toLocaleDateString("fr-CA");
  const isProduction = type==="Production";

  const STATUS_FR = { draft:"Brouillon", Draft:"Brouillon", sent:"Envoyé", Sent:"Envoyé", revised:"Révisé", Revised:"Révisé", approved:"Approuvé", Approved:"Approuvé", rejected:"Rejeté", Rejected:"Rejeté", paid:"Payé", Paid:"Payé", cancelled:"Annulé", Cancelled:"Annulé", pending:"En attente", Pending:"En attente" };
  const SERVICE_FR = { Hardscape:"Terrassement", Landscape:"Aménagement paysager", Landscaping:"Aménagement paysager", Excavation:"Excavation", Pool:"Piscine", Other:"Autre" };
  const CATEGORY_FR = { Material:"Matériau", Labor:"Main-d'œuvre", Labour:"Main-d'œuvre", Equipment:"Équipement", Subcontractor:"Sous-traitant", Tools:"Outils" };
  const sectionTotals = (estimate.sections||[]).filter(sec=>sec.type!=="group").map(sec=>({
    title: sec.title, description: sec.description||"", items: (sec.items||[]).filter(i=>i.type!=="subheading"),
    allItems: sec.items||[],
    total: sec.price != null ? sec.price : (sec.items||[]).filter(i=>i.type!=="subheading").reduce((a,i)=>a+(i.unitPrice||0)*(i.qty||1),0),
    costTotal: (sec.items||[]).filter(i=>i.type!=="subheading").reduce((a,i)=>a+(i.unitCost||0)*(i.qty||1),0),
  }));
  const subtotal = sectionTotals.reduce((a,s)=>a+s.total,0);
  const approvedCOs = (estimate.changeOrders||[]).filter(co=>co.status==="approved"||co.status==="manual");
  const coAmt = approvedCOs.reduce((a,co)=>a+(co.amount||0),0);
  const grandTotal = subtotal + coAmt;
  const tps = Math.round(grandTotal*0.05*100)/100;
  const tvq = Math.round(grandTotal*0.09975*100)/100;
  const grandTotalTax = grandTotal+tps+tvq;

  // (labour summary now handled inside productionSummaryPage)

  const logoHTML = settings?.logoBase64
    ? `<img src="${settings.logoBase64}" style="height:52px;width:auto;object-fit:contain;" />`
    : `<div style="width:52px;height:52px;background:#b91c1c;border-radius:8px;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:14px;">${(settings?.companyName||"TAF").split(" ").slice(0,3).map(w=>w[0]).join("")}</div>`;

  const miniHeader = `
<div class="mini-header">
  <div style="display:flex;align-items:center;gap:10px;">
    ${logoHTML}
    <div>
      <div style="font-size:13px;font-weight:700;">${settings?.companyName||""}</div>
      <div style="font-size:10px;color:#57534e;">${[settings?.phone,settings?.email].filter(Boolean).join(" · ")}</div>
    </div>
  </div>
  <div style="text-align:right;">
    <div style="font-size:16px;font-weight:700;color:#b91c1c;text-transform:uppercase;">${type}</div>
    <div style="font-size:11px;color:#57534e;">N° ${estimate.number||""} · ${estimate.title||""}</div>
  </div>
</div>`;

  const fullHeader = `
<div class="header">
  <div class="logo-block">
    ${logoHTML}
    <div>
      <div class="company-name">${settings?.companyName||""}</div>
      <div class="company-info">
        ${settings?.address||""}${settings?.city?", "+settings.city:""} ${settings?.province||""} ${settings?.postal||""}<br/>
        ${[settings?.phone,settings?.email].filter(Boolean).join(" · ")}<br/>
        ${type==="Facture"&&settings?.gst?"TPS / GST: "+settings.gst+"  ":""}${type==="Facture"&&settings?.qst?"TVQ / QST: "+settings.qst:""}
      </div>
    </div>
  </div>
  <div class="doc-info">
    <div class="doc-type">${type}</div>
    <div class="doc-meta">
      <strong>N°:</strong> ${estimate.number||""}<br/>
      <strong>Date:</strong> ${date}<br/>
      ${estimate.validUntil?`<strong>Valide jusqu'au:</strong> ${estimate.validUntil}<br/>`:""}
      <strong>Statut:</strong> <span class="badge">${STATUS_FR[estimate.status]||estimate.status||""}</span>
    </div>
  </div>
</div>
<div class="client-section">
  <div>
    <div class="section-label">Client</div>
    <div class="section-value">
      <strong>${customer?(customer.firstName+" "+customer.lastName):""}</strong><br/>
      ${customer?.company?customer.company+"<br/>":""}
      ${customer?.address?customer.address+"<br/>":""}
      ${customer?.city?(customer.city+", "+customer.province+" "+customer.postal+"<br/>"):""}
      ${[customer?.phone,customer?.email].filter(Boolean).join(" · ")}
    </div>
  </div>
  <div>
    <div class="section-label">Projet</div>
    <div class="section-value">
      <strong>${estimate.title||""}</strong><br/>
      Type: ${SERVICE_FR[estimate.serviceType]||estimate.serviceType||""}
    </div>
  </div>
</div>`;

  const _itemCols = (cfg.showLineItems ? 1 : 0) + (cfg.showLineItems&&cfg.showUnitCost ? 1 : 0) + (cfg.showLineItems&&cfg.showUnitPrice ? 1 : 0) + (cfg.showLineItems&&cfg.showLineItemTotals ? 1 : 0);
  const _totalCols = _itemCols + 1; // +1 for the Total section column
  const _sectionsBodyHTML = (estimate.sections||[]).filter(sec=>sec.type!=="group").map(sec=>{
    const allItems = sec.items||[];
    const pricingItems = allItems.filter(i=>i.type!=="subheading");
    const total = sec.price != null ? sec.price : pricingItems.reduce((a,i)=>a+(i.unitPrice||0)*(i.qty||1),0);
    return `
    <tr class="section-row">
      <td class="section-name"${cfg.showLineItems?` colspan="${_totalCols-1}"`:""}>${sec.title||""}${sec.description?`<div style="font-size:11px;font-weight:400;color:#57534e;margin-top:3px;">${sec.description}</div>`:""}</td>
      <td class="right section-total">${fmt(total)}</td>
    </tr>
    ${cfg.showLineItems ? allItems.map(it=>{
      if(it.type==="subheading") return `<tr><td colspan="${_totalCols}" style="padding:5px 9px 3px;font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#a8a29e;border-top:1px solid #e5e3df;background:#fafaf9;">${it.label||""}</td></tr>`;
      return `<tr style="background:#fafaf9;">
      <td style="padding-left:18px;font-size:11.5px;color:#57534e;">${it.name||""}${it.qty&&it.qty!==1?` <span style="color:#a8a29e">× ${it.qty}</span>`:"× 1"}</td>
      ${cfg.showUnitCost ? `<td class="right" style="font-size:11px;color:#a8a29e;">${fmt(it.unitCost)}</td>` : ""}
      ${cfg.showUnitPrice ? `<td class="right" style="font-size:11px;">${fmt(it.unitPrice)}</td>` : ""}
      ${cfg.showLineItemTotals ? `<td class="right" style="font-size:11px;">${fmt((it.unitPrice||0)*(it.qty||1))}</td>` : ""}
      <td></td>
    </tr>`;
    }).join("") : ""}
    ${cfg.showLineItems&&cfg.showSectionSubtotals ? `
    <tr class="subtotal-row"><td colspan="${_totalCols-1}" class="right"><strong>Sous-total</strong></td><td class="right"><strong>${fmt(total)}</strong></td></tr>` : ""}`;
  }).join("");

  const estimateBody = `
<table class="sections-table">
  <thead><tr>
    ${cfg.showLineItems ? `<th>Description</th>` : `<th>Section</th>`}
    ${cfg.showLineItems&&cfg.showUnitCost ? `<th class="right" style="width:90px">Coût unit.</th>` : ""}
    ${cfg.showLineItems&&cfg.showUnitPrice ? `<th class="right" style="width:90px">Prix unit.</th>` : ""}
    ${cfg.showLineItems&&cfg.showLineItemTotals ? `<th class="right" style="width:80px">Total</th>` : ""}
    <th class="right" style="width:${cfg.showLineItems?"110px":"30%"}">Total section</th>
  </tr></thead>
  <tbody>
    ${_sectionsBodyHTML}
    ${approvedCOs.length>0?`
    <tr style="background:#fef3c7;"><td colspan="${_totalCols}" style="padding:6px 12px;font-size:11px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:.06em;">Avenants / Change Orders</td></tr>
    ${approvedCOs.map(co=>`
    <tr class="section-row" style="background:#fffbeb;">
      <td class="section-name"${_totalCols>2?` colspan="${_totalCols-1}"`:""}  style="color:#92400e;">${co.number} — ${co.description||"Avenant"}</td>
      <td class="right section-total" style="color:#d97706;">+${fmt(co.amount)}</td>
    </tr>`).join("")}`:""}
  </tbody>
</table>`;


  const totalsBlock = cfg.showTaxes ? `
<div class="totals">
  <div class="totals-box">
    ${coAmt!==0?`<div class="total-row" style="color:#92400e;"><span>Sous-total (base)</span><span>${fmt(subtotal)}</span></div><div class="total-row" style="color:#d97706;"><span>Avenants approuvés</span><span>+${fmt(coAmt)}</span></div>`:""}
    <div class="total-row"><span>Sous-total</span><span>${fmt(grandTotal)}</span></div>
    <div class="total-row tax"><span>TPS (5%)${type==="Facture"&&settings?.gst?"  No: "+settings.gst:""}</span><span>${fmt(tps)}</span></div>
    <div class="total-row tax"><span>TVQ (9,975%)${type==="Facture"&&settings?.qst?"  No: "+settings.qst:""}</span><span>${fmt(tvq)}</span></div>
    <div class="total-row grand"><span>Total</span><span>${fmt(grandTotalTax)}</span></div>
  </div>
</div>` : "";

  // ── Production summary — split into two groups ──
  const showSupplier = cfg.showSupplier !== false; // default true
  const allItemsMap = {};
  if(isProduction){
    (estimate.sections||[]).filter(sec=>sec.type!=="group").forEach(sec=>{
      (sec.items||[]).filter(it=>it.type!=="subheading").forEach(it=>{
        const supplier = it.supplier || (priceList||[]).find(p=>p.name===it.name)?.supplier || "";
        const key = it.name+"||"+(it.category||"")+"||"+supplier;
        if(!allItemsMap[key]) allItemsMap[key]={name:it.name,category:it.category||"",supplier,qty:0,unit:it.unit||"",unitCost:it.unitCost||0,unitPrice:it.unitPrice||0};
        allItemsMap[key].qty+=(+it.qty||1);
      });
    });
  }
  const allItemsRows = Object.values(allItemsMap).sort((a,b)=>a.name.localeCompare(b.name));
  const matSubRows    = allItemsRows.filter(r=>r.category==="Material"||r.category==="Subcontractor");
  const labEquipRows  = allItemsRows.filter(r=>r.category==="Labor"||r.category==="Equipment"||r.category==="Labour"||(!r.category));

  const summaryTable = (rows, title, withSupplier) => {
    if (!rows.length) return "";
    // fixed cols: Description [+ Fournisseur] + Catégorie + Qté
    const fixedCols = withSupplier ? 4 : 3;
    const optCols = (cfg.showUnitCost?1:0) + (cfg.showUnitPrice?1:0) + (cfg.showLineItemTotals?1:0);
    const totalCols = fixedCols + optCols;

    const buildRow = (r) => {
      const supCol = withSupplier ? "<td style='font-size:11px;color:#78716c;'>" + (r.supplier||"") + "</td>" : "";
      return "<tr>" +
        "<td style='font-weight:500'>" + r.name + "</td>" +
        supCol +
        "<td><span class='badge'>" + (CATEGORY_FR[r.category]||r.category) + "</span></td>" +
        "<td class='right'>" + r.qty + "</td>" +
        (cfg.showUnitCost ? "<td class='right'>" + fmt(r.unitCost) + "</td>" : "") +
        (cfg.showUnitPrice ? "<td class='right'>" + fmt(r.unitPrice) + "</td>" : "") +
        (cfg.showLineItemTotals ? "<td class='right'>" + fmt(r.unitCost*r.qty) + "</td>" : "") +
        "</tr>";
    };

    let rowsHTML = "";
    if(withSupplier){
      const sorted = [...rows].sort((a,b)=>{
        const sa = a.supplier||""; const sb = b.supplier||"";
        if(sa===sb) return a.name.localeCompare(b.name);
        if(!sa) return 1; if(!sb) return -1;
        return sa.localeCompare(sb);
      });
      let lastSupplier = null;
      rowsHTML = sorted.map(r=>{
        const sup = r.supplier||"";
        let headerRow = "";
        if(sup!==lastSupplier){
          lastSupplier=sup;
          headerRow = "<tr style='background:#f5f5f4;'><td colspan='" + totalCols + "' style='padding:4px 8px;font-size:11px;font-weight:700;color:#57534e;letter-spacing:.04em;'>" + (sup||"—") + "</td></tr>";
        }
        return headerRow + buildRow(r);
      }).join("");
    } else {
      rowsHTML = rows.map(buildRow).join("");
    }

    const sub = fmt(rows.reduce((a,r)=>a+r.unitCost*r.qty,0));
    const subtotalRow = cfg.showSectionSubtotals
      ? "<tr class='subtotal-row'><td colspan='" + (totalCols-1) + "' class='right'><strong>Sous-total</strong></td><td class='right'><strong>" + sub + "</strong></td></tr>"
      : "";

    // colgroup: fixed cols + optional cols
    const cgFixed = withSupplier
      ? "<col><col style='width:110px'><col style='width:100px'><col style='width:60px'>"
      : "<col><col style='width:100px'><col style='width:60px'>";
    const cgOpt = (cfg.showUnitCost?"<col style='width:85px'>":"") + (cfg.showUnitPrice?"<col style='width:85px'>":"") + (cfg.showLineItemTotals?"<col style='width:100px'>":"");
    const colgroup = "<colgroup>" + cgFixed + cgOpt + "</colgroup>";

    const thFixed = withSupplier
      ? "<th>Description</th><th>Fournisseur</th><th>Cat\u00e9gorie</th><th class='right'>Qt\u00e9</th>"
      : "<th>Description</th><th>Cat\u00e9gorie</th><th class='right'>Qt\u00e9</th>";
    const thOpt = (cfg.showUnitCost?"<th class='right'>Co\u00fbt unit.</th>":"") + (cfg.showUnitPrice?"<th class='right'>Prix unit.</th>":"") + (cfg.showLineItemTotals?"<th class='right'>Total co\u00fbt</th>":"");
    const thead = "<thead><tr>" + thFixed + thOpt + "</tr></thead>";

    return (
      "<div style='margin-bottom:24px;'>" +
      "<div class='page2-title'>" + title + "</div>" +
      "<table style='margin-top:10px;width:100%;border-collapse:collapse;table-layout:fixed;'>" +
      colgroup + thead +
      "<tbody>" + rowsHTML + subtotalRow +
      "</tbody></table></div>"
    );
  };

  const productionSummaryPage = isProduction ? `
<div style="padding-top:0;">
  ${miniHeader}
  ${summaryTable(matSubRows,"Matériaux et sous-traitants",showSupplier)}
  ${summaryTable(labEquipRows,"Main-d'œuvre et équipement",false)}
  <div class="subtotal-row" style="display:flex;justify-content:flex-end;padding:8px 0;margin-top:4px;">
    <span style="font-weight:700;font-size:13px;">Total général (coût): ${fmt(allItemsRows.reduce((a,r)=>a+r.unitCost*r.qty,0))}</span>
  </div>
</div>` : "";

  const sigHTML = settings?.signatureBase64
    ? `<img src="${settings.signatureBase64}" style="height:48px;width:auto;object-fit:contain;display:block;margin-bottom:4px;" />`
    : `<div style="height:48px;"></div>`;

  const signatureBlock = `
<div class="signature-block">
  <div class="sig-box">
    <div class="sig-label">Représentant autorisé — ${settings?.companyName||""}</div>
    <div class="sig-line">${sigHTML}</div>
    <div class="sig-name">${settings?.authorizedName||settings?.companyName||""}</div>
    <div class="sig-date">Date: ${date}</div>
  </div>
  <div class="sig-box">
    <div class="sig-label">Client — ${customer?(customer.firstName+" "+customer.lastName):""}</div>
    <div class="sig-line"><div style="height:48px;"></div></div>
    <div class="sig-name">${customer?(customer.firstName+" "+customer.lastName):""}</div>
    <div class="sig-date">Date: ___________________________</div>
  </div>
</div>`;

  // Reference materials page (estimate only)
  const refPhotos = (!isProduction && cfg.showReferenceMaterials) ? (Array.isArray(estimate.referencePhotos) ? estimate.referencePhotos : []) : [];
  const referenceMaterialsPage = refPhotos.length > 0 ? `
<div style="page-break-before:always;padding-top:24px;">
  ${miniHeader}
  <div class="page2-title">Matériaux de référence</div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:14px;">
    ${refPhotos.map(p=>`
    <div style="text-align:center;">
      <img src="${p.photoURL}" alt="${(p.name||"").replace(/"/g,"&quot;")}" style="width:100%;height:130px;object-fit:cover;border-radius:6px;margin-bottom:6px;display:block;"/>
      <div style="font-size:12px;font-weight:600;color:#1c1917;">${p.name||""}</div>
      ${p.caption?`<div style="font-size:11px;color:#57534e;margin-top:2px;line-height:1.4;">${p.caption}</div>`:""}
    </div>`).join("")}
  </div>
</div>` : "";

  // Design plans page (estimate only) — full-width stacked, one plan per print block
  const dpPhotos = (!isProduction && cfg.showDesignPlans) ? (Array.isArray(estimate.designPlans) ? estimate.designPlans : []) : [];
  const designPlansPage = dpPhotos.length > 0 ? `
<div style="page-break-before:always;padding-top:24px;">
  ${miniHeader}
  <div class="page2-title">Plans &amp; Rendus</div>
  ${dpPhotos.map(p=>`
  <div style="page-break-inside:avoid;margin-bottom:24px;">
    <img src="${p.photoURL}" alt="${(p.name||"").replace(/"/g,"&quot;")}" style="width:100%;max-height:620px;object-fit:contain;border-radius:6px;display:block;background:#f8f7f5;"/>
    ${p.name||p.caption?`<div style="margin-top:8px;text-align:center;">
      ${p.name?`<div style="font-size:13px;font-weight:600;color:#1c1917;">${p.name}</div>`:""}
      ${p.caption?`<div style="font-size:11px;color:#57534e;margin-top:3px;">${p.caption}</div>`:""}
    </div>`:""}
  </div>`).join("")}
</div>` : "";

  // Fix 2: Terms page with signatures at the end
  const termsPage = (!isProduction && settings?.terms) ? `
<div style="page-break-before:always;padding-top:24px;">
  ${miniHeader}
  <div class="page2-title">Modalités et Conditions</div>
  <div class="terms-body">${settings.terms.replace(/\n/g,"<br/>")}</div>
  ${signatureBlock}
</div>` : "";

  // If no terms page, put signature on page 1
  const page1Signature = (!isProduction && !settings?.terms) ? signatureBlock : "";

  return `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"/>
<title>${type} — ${estimate.title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;font-size:12px;color:#1c1917;background:#fff;padding:28px 36px;}
  @page{size:letter;margin:15mm 18mm;@bottom-right{content:"Page " counter(page) " / " counter(pages);font-size:9px;color:#a8a29e;font-family:'DM Sans',sans-serif;}}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #b91c1c;}
  .mini-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:18px;padding-bottom:10px;border-bottom:1px solid #e5e3df;}
  .logo-block{display:flex;align-items:center;gap:14px;}
  .company-name{font-size:16px;font-weight:700;}
  .company-info{font-size:10.5px;color:#57534e;margin-top:3px;line-height:1.65;}
  .doc-info{text-align:right;}
  .doc-type{font-size:19px;font-weight:700;color:#b91c1c;text-transform:uppercase;letter-spacing:.04em;}
  .doc-meta{font-size:10.5px;color:#57534e;margin-top:4px;line-height:1.8;}
  .client-section{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px;padding:11px 14px;background:#f8f7f5;border-radius:8px;}
  .section-label{font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#a8a29e;margin-bottom:4px;}
  .section-value{font-size:12px;color:#1c1917;line-height:1.6;}
  table{width:100%;border-collapse:collapse;table-layout:fixed;}
  th{padding:6px 9px;text-align:left;font-size:9.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#a8a29e;background:#f8f7f5;border-bottom:2px solid #e5e3df;}
  td{padding:7px 9px;font-size:12px;border-bottom:1px solid #e5e3df;vertical-align:middle;}
  .right{text-align:right;}
  .num{color:#a8a29e;font-size:10px;text-align:center;}
  .badge{display:inline-block;padding:2px 6px;border-radius:8px;font-size:9.5px;font-weight:600;background:#f3f4f6;color:#6b7280;}
  .sections-table{margin-bottom:4px;}
  .section-row td{padding:10px 9px;}
  .section-name{font-weight:600;font-size:12.5px;}
  .section-total{font-weight:600;font-size:12.5px;}
  .section-block{margin-bottom:16px;}
  .section-title{font-size:12.5px;font-weight:700;background:#1a0a0a;color:white;padding:7px 11px;border-radius:4px 4px 0 0;}
  .subtotal-row td{background:#f8f7f5;border-top:1px solid #e5e3df;padding:6px 9px;}
  .totals{display:flex;justify-content:flex-end;margin-top:18px;}
  .totals-box{width:280px;border:1px solid #e5e3df;border-radius:8px;overflow:hidden;}
  .total-row{display:flex;justify-content:space-between;padding:7px 13px;font-size:12px;color:#57534e;border-bottom:1px solid #e5e3df;}
  .total-row:last-child{border-bottom:none;}
  .total-row.grand{font-weight:700;font-size:13px;color:#1c1917;background:#f8f7f5;}
  .total-row.tax{font-size:10.5px;color:#a8a29e;}
  .signature-block{display:grid;grid-template-columns:1fr 1fr;gap:36px;margin-top:36px;padding-top:20px;border-top:1px solid #e5e3df;}
  .sig-box{display:flex;flex-direction:column;gap:4px;}
  .sig-label{font-size:9.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#a8a29e;margin-bottom:6px;}
  .sig-line{border-bottom:1px solid #1c1917;min-height:50px;display:flex;align-items:flex-end;padding-bottom:3px;}
  .sig-name{font-size:10.5px;font-weight:600;color:#1c1917;margin-top:4px;}
  .sig-date{font-size:10.5px;color:#a8a29e;margin-top:2px;}
  .footer{margin-top:18px;padding-top:10px;border-top:1px solid #e5e3df;text-align:center;font-size:10px;color:#a8a29e;}
  .page2-title{font-size:16px;font-weight:700;margin:10px 0 3px;}
  .page2-sub{font-size:11px;color:#a8a29e;}
  .terms-body{font-size:11.5px;color:#57534e;line-height:1.8;margin-top:10px;white-space:pre-line;}
  .no-print{margin-bottom:16px;display:flex;gap:8px;}
  @media print{.no-print{display:none;}button{display:none !important;}body{margin:0;padding:0;}@page{margin:15mm;}}
</style></head><body>
<button onclick="window.print()" style="position:fixed;top:20px;right:20px;padding:10px 20px;background:#2563eb;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;z-index:999;">Télécharger PDF</button>
<div class="no-print">
  <button onclick="window.print()" style="padding:8px 18px;background:#b91c1c;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;font-weight:600;">🖨️ Imprimer / Print</button>
  <button onclick="window.close()" style="padding:8px 18px;background:#f3f4f6;color:#1c1917;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;">✕ Fermer</button>
</div>
${isProduction ? productionSummaryPage : `${fullHeader}${estimateBody}${totalsBlock}${page1Signature}
<div class="footer">${settings?.companyName||""} ${settings?.website?"· "+settings.website:""} ${settings?.email?"· "+settings.email:""} · Document généré le ${date}</div>
${referenceMaterialsPage}${designPlansPage}${termsPage}`}
</body></html>`;
}

// ─── TRUCK REPORT HTML GENERATOR ─────────────────────────────────────────────
export function generateTruckReportHTML(job, events, date, settings) {
  const isFr = (settings?.lang || "fr") !== "en";
  const L = isFr
    ? { title:"Journal de camions", entrants:"Entrants", sortants:"Sortants", material:"Matériau", loads:"Voyages", tonnage:"Total (t.m.)", time:"Heure", employee:"Employé", direction:"Direction", details:"Détail des voyages", totals:"Totaux", generatedOn:"Document généré le", printBtn:"Imprimer / Print", closeBtn:"✕ Fermer" }
    : { title:"Daily Truck Log", entrants:"Inbound", sortants:"Outbound", material:"Material", loads:"Loads", tonnage:"Total (t.m.)", time:"Time", employee:"Employee", direction:"Direction", details:"Detail log", totals:"Totals", generatedOn:"Generated on", printBtn:"Print", closeBtn:"✕ Close" };

  const fmtDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("fr-CA");
  const logoHTML = settings?.logoBase64
    ? `<img src="${settings.logoBase64}" style="height:48px;width:auto;object-fit:contain;" />`
    : `<div style="width:48px;height:48px;background:#b91c1c;border-radius:8px;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:13px;">${(settings?.companyName||"TAF").split(" ").slice(0,3).map(w=>w[0]).join("")}</div>`;

  const truckEvents = (events || []).filter(e => e.eventType === "truck" && e.date === date)
    .sort((a, b) => (a.loggedAt?.seconds || 0) - (b.loggedAt?.seconds || 0));

  const inEvents = truckEvents.filter(e => e.direction === "in");
  const outEvents = truckEvents.filter(e => e.direction === "out");
  const sumT = (evts) => evts.reduce((a, e) => a + (parseFloat(e.tonnage) || 0), 0);

  const breakdownRows = (evts, label) => {
    const map = {};
    evts.forEach(e => {
      if (!map[e.materialType]) map[e.materialType] = { count: 0, tonnage: 0 };
      map[e.materialType].count++;
      map[e.materialType].tonnage += parseFloat(e.tonnage) || 0;
    });
    return Object.entries(map).map(([mat, info]) =>
      `<tr><td style="padding-left:20px;color:#57534e;">${label} — ${mat}</td><td style="text-align:center;">${info.count}</td><td style="text-align:right;">${info.tonnage.toFixed(1)}</td></tr>`
    ).join("");
  };

  const summaryRows =
    `<tr style="background:#fef2f2;"><td style="font-weight:700;">↓ ${L.entrants}</td><td style="text-align:center;font-weight:700;">${inEvents.length}</td><td style="text-align:right;font-weight:700;">${sumT(inEvents).toFixed(1)}</td></tr>`
    + breakdownRows(inEvents, "↓ " + L.entrants)
    + `<tr style="background:#f8f7f5;"><td style="font-weight:700;">↑ ${L.sortants}</td><td style="text-align:center;font-weight:700;">${outEvents.length}</td><td style="text-align:right;font-weight:700;">${sumT(outEvents).toFixed(1)}</td></tr>`
    + breakdownRows(outEvents, "↑ " + L.sortants);

  const detailRows = truckEvents.map(e => {
    const time = e.loggedAt?.seconds
      ? new Date(e.loggedAt.seconds * 1000).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })
      : "—";
    return `<tr><td>${time}</td><td>${e.loggedBy || "—"}</td><td>${e.direction === "in" ? "↓ " + L.entrants : "↑ " + L.sortants}</td><td>${e.materialType || "—"}</td><td style="text-align:right;">${parseFloat(e.tonnage || 0).toFixed(1)}</td></tr>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="${isFr ? "fr" : "en"}"><head><meta charset="UTF-8"/>
<title>${L.title} — ${job?.title || ""}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;font-size:12px;color:#1c1917;background:#fff;padding:28px 36px;max-width:760px;margin:0 auto;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:14px;border-bottom:2px solid #b91c1c;}
  .logo-block{display:flex;align-items:center;gap:14px;}
  .company-name{font-size:16px;font-weight:700;}
  .company-info{font-size:10.5px;color:#57534e;margin-top:3px;line-height:1.65;}
  .doc-type{font-size:19px;font-weight:700;color:#b91c1c;text-transform:uppercase;letter-spacing:.04em;}
  .doc-meta{font-size:11px;color:#57534e;margin-top:4px;line-height:1.8;}
  h2{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#57534e;margin:20px 0 8px;}
  table{width:100%;border-collapse:collapse;font-size:12px;}
  th{background:#f8f7f5;padding:7px 10px;text-align:left;font-weight:600;border-bottom:2px solid #e7e5e4;}
  td{padding:6px 10px;border-bottom:1px solid #f0efed;}
  tr:last-child td{border-bottom:none;}
  .totals-row td{font-weight:700;background:#f8f7f5;border-top:2px solid #e7e5e4;}
  .footer{margin-top:28px;padding-top:12px;border-top:1px solid #e7e5e4;font-size:10.5px;color:#78716c;text-align:center;}
  .no-print{margin-bottom:20px;display:flex;gap:8px;}
  @media print{.no-print{display:none!important;}}
</style>
</head><body>
<div class="no-print">
  <button onclick="window.print()" style="padding:8px 18px;background:#b91c1c;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;font-weight:600;">${L.printBtn}</button>
  <button onclick="window.close()" style="padding:8px 18px;background:#f3f4f6;color:#1c1917;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit;">${L.closeBtn}</button>
</div>
<div class="header">
  <div class="logo-block">
    ${logoHTML}
    <div>
      <div class="company-name">${settings?.companyName || ""}</div>
      <div class="company-info">${[settings?.phone, settings?.email].filter(Boolean).join(" · ")}</div>
    </div>
  </div>
  <div style="text-align:right;">
    <div class="doc-type">${L.title}</div>
    <div class="doc-meta">${job?.title || ""}<br/>${fmtDate(date)}</div>
  </div>
</div>
<h2>${isFr ? "Sommaire" : "Summary"}</h2>
<table>
  <thead><tr><th>${L.material}</th><th style="text-align:center;">${L.loads}</th><th style="text-align:right;">${L.tonnage}</th></tr></thead>
  <tbody>${summaryRows}</tbody>
  <tfoot><tr class="totals-row"><td>${L.totals}</td><td style="text-align:center;">${truckEvents.length}</td><td style="text-align:right;">${sumT(truckEvents).toFixed(1)}</td></tr></tfoot>
</table>
${truckEvents.length > 0 ? `<h2>${L.details}</h2>
<table>
  <thead><tr><th>${L.time}</th><th>${L.employee}</th><th>${L.direction}</th><th>${L.material}</th><th style="text-align:right;">${L.tonnage}</th></tr></thead>
  <tbody>${detailRows}</tbody>
</table>` : ""}
<div class="footer">${settings?.companyName || ""} · ${L.generatedOn} ${new Date().toLocaleDateString("fr-CA")}</div>
</body></html>`;
}

export const PHASE_CODES = [
  { code: "site-prep",         labelFr: "Préparation de site",    labelEn: "Site Prep" },
  { code: "bulk-excavation",   labelFr: "Excavation en masse",    labelEn: "Bulk Excavation" },
  { code: "backfill",          labelFr: "Remblayage",             labelEn: "Backfill" },
  { code: "screed-paver-base", labelFr: "Lit de pose",            labelEn: "Screed / Paver Base" },
  { code: "paver-install",     labelFr: "Pose de pavé",           labelEn: "Paver Install" },
  { code: "drainage",          labelFr: "Drainage",               labelEn: "Drainage" },
  { code: "retaining-wall",    labelFr: "Mur de soutènement",     labelEn: "Retaining Wall" },
  { code: "stone-wall",        labelFr: "Mur de pierre",          labelEn: "Stone Wall" },
  { code: "finishing",         labelFr: "Finition",               labelEn: "Finishing" },
  { code: "cleanup",           labelFr: "Nettoyage de chantier",  labelEn: "Site Cleanup" },
  { code: "water-main",        labelFr: "Entrée d'eau",           labelEn: "Water Main" },
  { code: "deck-build",        labelFr: "Construction de deck",   labelEn: "Deck Build" },
  { code: "construction",      labelFr: "Construction",           labelEn: "Construction" },
  { code: "pool-installation", labelFr: "Installation de piscine",labelEn: "Pool Installation" },
  { code: "warranty-repair",   labelFr: "Travaux de garantie",    labelEn: "Warranty Repair" },
  { code: "other",             labelFr: "Autre",                  labelEn: "Other" },
];
